import { PageBanner, Section, SectionTitle, useDocument } from '@nanning/ui';
import { assetUrl, useT } from '../i18n';
import { findImage, useSiteData } from '../useSiteData';
import {} from './common';
import type { EsgDoc } from '../types';

export function Esg() {
  const { t, pick } = useT();
  const s = useSiteData();
  const doc = useDocument<EsgDoc>('group', 'esg.json');
  const error = s.error ?? doc.error;
  if (error) return <div className="mx-auto max-w-6xl px-5 py-24 text-danger-light">{error}</div>;
  if (!s.data || !doc.data) return <div className="mx-auto max-w-6xl px-5 py-24 text-muted">{t('state.loading')}</div>;

  const img = findImage(s.data.imagery, 'karst');
  const esg = doc.data;

  return (
    <>
      <PageBanner
        image={assetUrl(img.local)}
        imageAlt={pick(img.alt)}
        imageCredit={t('esg.banner.credit')}
        eyebrow={t('esg.eyebrow')}
        title={t('esg.title')}
        lead={t('esg.lead')}
      />

      <Section id="pillars">
        <SectionTitle id="pillars" title={t('esg.pillars.title')} />
        <ul className="mt-12 grid gap-6 md:grid-cols-3">
          {esg.pillars.map((p) => (
            <li key={p.id} className="flex flex-col rounded-xl border border-line bg-bg p-8">
              <div className="text-sm font-semibold tracking-widest text-primary-light">{pick(p.title)}</div>
              <p className="mt-4 text-lg font-semibold leading-snug text-body">{pick(p.lead)}</p>
              <ul className="mt-6 space-y-3 border-t border-line pt-6 text-sm leading-relaxed text-muted">
                {p.points.map((x, i) => (
                  <li key={i} className="flex gap-3">
                    <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span>{pick(x)}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </Section>

      <Section id="programs" tone="surface">
        <SectionTitle id="programs" title={t('esg.programs.title')} lead={t('esg.programs.lead')} />
        <ul className="mt-12 grid gap-4 md:grid-cols-3">
          {esg.programs.map((p) => (
            <li key={p.id} className="flex flex-col rounded-xl border border-line bg-bg p-6">
              <h3 className="text-lg font-semibold leading-snug text-body">{pick(p.title)}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted">{pick(p.body)}</p>
              <div className="mt-auto pt-5">
              </div>
            </li>
          ))}
        </ul>
      </Section>

      <Section id="reports">
        <SectionTitle id="reports" title={t('esg.reports.title')} lead={t('esg.reports.lead')} />
        <ul className="mt-12 divide-y divide-line border-y border-line">
          {esg.reports.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-4 py-6">
              <span className="text-base font-semibold text-body">{pick(r.title)}</span>
              <div className="flex items-center gap-3">
                <span className="rounded bg-surface px-2 py-0.5 text-xs text-muted-light">{pick(r.statusLabel)}</span>
                <button
                  type="button"
                  disabled
                  className="rounded-md border border-line-strong px-4 py-2 text-sm text-muted disabled:cursor-not-allowed"
                >
                  {t('esg.reports.soon')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </Section>
    </>
  );
}
